// import "./App.css";
import "./Tailwind.css";
// App.js
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

function formatTime(seconds) {
  const total = Math.max(0, Math.floor(Number(seconds) || 0));
  const s = String(total % 60).padStart(2, "0");
  const m = String(Math.floor((total / 60) % 60)).padStart(2, "0");
  const h = Math.floor(total / 3600);

  return h > 0 ? `${h}:${m}:${s}` : `${m}:${s}`;
}

function getYouTubeVideoId(value) {
  try {
    const url = new URL(value);
    const hostname = url.hostname.replace(/^(www\.|m\.|music\.)/, "");

    if (hostname === "youtu.be") return url.pathname.split("/")[1] || "";

    if (hostname === "youtube.com") {
      const pathVideoId = url.pathname.match(
        /^\/(?:embed|shorts|live)\/([^/?]+)/,
      )?.[1];
      return url.searchParams.get("v") || pathVideoId || "";
    }
  } catch {
    return (
      value.match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([\w-]{11})/)?.[1] ||
      ""
    );
  }

  return "";
}

function getInitialYouTubeUrl() {
  const videoId = new URLSearchParams(window.location.search).get("v") || "";
  return /^[\w-]{11}$/.test(videoId)
    ? `https://www.youtube.com/watch?v=${videoId}`
    : "";
}

function getShareUrl(videoId) {
  const shareUrl = new URL(window.location.href);
  shareUrl.search = "";
  shareUrl.hash = "";
  shareUrl.searchParams.set("v", videoId);
  return shareUrl.toString();
}

function CopyButton({ text, label, copyKey, copiedKey, onCopy, className = "" }) {
  return (
    <button
      type="button"
      onClick={(event) => {
        event.stopPropagation();
        onCopy(text, copyKey);
      }}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-400 opacity-60 transition hover:bg-slate-100 hover:text-slate-900 hover:opacity-100 dark:text-slate-500 dark:hover:bg-slate-700 dark:hover:text-slate-100 ${className}`}
      aria-label={label}
      title={label}
    >
      {copiedKey === copyKey ? (
        <span className="text-sm text-emerald-600" aria-hidden="true">
          ✓
        </span>
      ) : (
        <svg
          viewBox="0 0 24 24"
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <rect x="9" y="9" width="11" height="11" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </button>
  );
}

function ChapterCard({ chapter, chapterIndex, onJump, onCopy, copiedKey, chapterRef }) {
  return (
    <section ref={chapterRef} className="border-t border-slate-200 pt-5 first:border-0 first:pt-0 dark:border-slate-700">
      <div className="relative">
        <div className="flex items-baseline justify-between gap-3 pr-7">
          <h3 className="min-w-0 font-book text-xl font-black leading-snug text-slate-950 dark:text-slate-100 sm:text-2xl">
            {chapterIndex + 1}. {chapter.title}
          </h3>
          <button
            type="button"
            onClick={() => onJump(chapter.start_timestamp)}
            className="shrink-0 text-sm font-medium text-blue-600 underline-offset-2 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
            aria-label={`Play chapter at ${formatTime(chapter.start_timestamp)}`}
          >
            {formatTime(chapter.start_timestamp)}
          </button>
        </div>
        <CopyButton
          text={`${chapterIndex + 1}. ${chapter.title}`}
          label="Copy chapter heading"
          copyKey={`chapter-${chapterIndex}`}
          copiedKey={copiedKey}
          onCopy={onCopy}
          className="absolute -bottom-1 -right-2"
        />
      </div>

      {Array.isArray(chapter.subtopics) && chapter.subtopics.length > 0 && (
        <div className="mt-2 space-y-4">
          {chapter.subtopics.map((subtopic, subtopicIndex) => {
            const subtopicKey = `chapter-${chapterIndex}-subtopic-${subtopicIndex}`;
            const quotes = Array.isArray(subtopic.quotes) ? subtopic.quotes : [];
            const summary = subtopic.summary || subtopic.transcript || "";
            const subtopicNotes = [
              `${chapterIndex + 1}.${subtopicIndex + 1} ${subtopic.title}`,
              summary,
              ...quotes.map((quote) => `“${quote}”`),
            ]
              .filter(Boolean)
              .join("\n\n");

            return (
              <article key={subtopicKey} className="relative pl-4 sm:pl-6">
                <div className="relative">
                  <div className="flex items-baseline justify-between gap-3 pr-7">
                    <h4 className="min-w-0 font-book text-xl font-bold leading-[25px] text-slate-900 dark:text-slate-100 sm:text-[22px] sm:leading-7">
                      {chapterIndex + 1}.{subtopicIndex + 1} {subtopic.title}
                    </h4>
                    <button
                      type="button"
                      onClick={() => onJump(subtopic.start_timestamp)}
                      className="shrink-0 text-sm font-medium text-blue-600 underline-offset-2 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                      aria-label={`Play subchapter at ${formatTime(subtopic.start_timestamp)}`}
                    >
                      {formatTime(subtopic.start_timestamp)}
                    </button>
                  </div>
                  <CopyButton
                    text={`${chapterIndex + 1}.${subtopicIndex + 1} ${subtopic.title}`}
                    label="Copy subchapter heading"
                    copyKey={`${subtopicKey}-heading`}
                    copiedKey={copiedKey}
                    onCopy={onCopy}
                    className="absolute -bottom-1 -right-2"
                  />
                </div>

                {summary && (
                  <p className="mt-2 font-book text-lg font-normal leading-7 text-slate-700 dark:text-slate-300 sm:text-xl">
                    {summary}
                  </p>
                )}

                {quotes.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {quotes.map((quote, quoteIndex) => (
                      <blockquote
                        key={`${subtopicKey}-quote-${quoteIndex}`}
                        className="group relative border-l-4 border-slate-200 py-1 pl-4 font-book text-lg italic leading-7 text-slate-700 dark:border-slate-700 dark:text-slate-300 sm:text-xl"
                      >
                        “{quote}”
                        <CopyButton
                          text={quote}
                          label="Copy quote"
                          copyKey={`${subtopicKey}-quote-${quoteIndex}`}
                          copiedKey={copiedKey}
                          onCopy={onCopy}
                          className="absolute -bottom-1 -right-2 group-hover:opacity-100"
                        />
                      </blockquote>
                    ))}
                  </div>
                )}

                <CopyButton
                  text={subtopicNotes}
                  label="Copy complete subchapter"
                  copyKey={`${subtopicKey}-all`}
                  copiedKey={copiedKey}
                  onCopy={onCopy}
                  className="absolute -bottom-1 right-6"
                />
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

export default function VideoTOC() {
  const playerRef = useRef(null);
  const playerIframeRef = useRef(null);
  const chapterRefs = useRef([]);
  const hasAutoStartedRef = useRef(false);
  const baseUrl =
    window.location.hostname === "localhost"
      ? "http://localhost:5002"
      : "https://fwdmailer.com";
  const [youtubeUrl, setYoutubeUrl] = useState(getInitialYouTubeUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [tocData, setTocData] = useState(null);
  const [videoId, setVideoId] = useState("");
  const [copiedKey, setCopiedKey] = useState("");
  const [shareStatus, setShareStatus] = useState("");
  const [error, setError] = useState("");
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = window.localStorage.getItem("skimble-dark-mode");
      if (saved !== null) return saved === "true";
    } catch {
      // Storage can be unavailable in privacy-restricted browsers.
    }
    return window.matchMedia?.("(prefers-color-scheme: dark)").matches || false;
  });
  const [readingProgress, setReadingProgress] = useState({ percent: 0, chapterIndex: 0 });

  const chapters = useMemo(() => tocData?.chapters || [], [tocData]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
    try {
      window.localStorage.setItem("skimble-dark-mode", String(darkMode));
    } catch {
      // Theme still works for this session when storage is unavailable.
    }
  }, [darkMode]);

  useEffect(() => {
    if (!tocData || chapters.length === 0) return undefined;

    let frameId = 0;
    const updateProgress = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const tocElement = document.getElementById("skimble-toc");
        if (!tocElement) return;

        const tocTop = window.scrollY + tocElement.getBoundingClientRect().top;
        const scrollableDistance = Math.max(1, tocElement.offsetHeight - window.innerHeight);
        const percent = Math.round(Math.min(1, Math.max(0, (window.scrollY - tocTop) / scrollableDistance)) * 100);
        const readingLine = window.innerHeight * 0.34;
        let chapterIndex = 0;
        chapterRefs.current.forEach((chapterElement, index) => {
          if (chapterElement && chapterElement.getBoundingClientRect().top <= readingLine) {
            chapterIndex = index;
          }
        });
        setReadingProgress({ percent, chapterIndex });
      });
    };

    updateProgress();
    window.addEventListener("scroll", updateProgress, { passive: true });
    window.addEventListener("resize", updateProgress);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener("scroll", updateProgress);
      window.removeEventListener("resize", updateProgress);
    };
  }, [chapters.length, tocData]);

  const handleCopy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(""), 1500);
    } catch {
      setError("Could not copy to clipboard");
    }
  };

  const handleJumpToTime = (seconds) => {
    if (!videoId) return;

    const targetSeconds = Math.max(0, Math.floor(Number(seconds) || 0));
    const playerWindow = playerIframeRef.current?.contentWindow;

    playerWindow?.postMessage(
      JSON.stringify({ event: "command", func: "seekTo", args: [targetSeconds, true] }),
      "https://www.youtube-nocookie.com",
    );
    playerWindow?.postMessage(
      JSON.stringify({ event: "command", func: "playVideo", args: [] }),
      "https://www.youtube-nocookie.com",
    );
    playerRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSkimble = useCallback(async (urlOverride, { forceRefresh = false } = {}) => {
    const trimmedUrl = (typeof urlOverride === "string" ? urlOverride : youtubeUrl).trim();
    if (!trimmedUrl) return;

    const nextVideoId = getYouTubeVideoId(trimmedUrl);
    if (!/^[\w-]{11}$/.test(nextVideoId)) {
      setError("Enter a valid YouTube URL");
      return;
    }

    setVideoId(nextVideoId);
    window.history.replaceState({}, "", getShareUrl(nextVideoId));

    setIsLoading(true);
    setError("");
    setCopiedKey("");
    setShareStatus("");
    setTocData(null);

    try {
      const response = await fetch(`${baseUrl}/summarize-youtube`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          url: trimmedUrl,
          forceRefresh,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate table of contents");
      }

      if (!response.body) {
        throw new Error("Streaming responses are not supported by this browser");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let receivedToc = false;

      const handleEvent = (line) => {
        if (!line.trim()) return;

        const event = JSON.parse(line);
        if (event.type === "error") {
          throw new Error(event.error || "Failed to generate table of contents");
        }
        if (event.type !== "toc" || !event.data) return;

        receivedToc = true;
        setTocData({
          title: event.data.title || "Untitled Video",
          chapters: Array.isArray(event.data.chapters)
            ? event.data.chapters
            : [],
        });
      };

      while (true) {
        const { value, done } = await reader.read();
        buffer += decoder.decode(value, { stream: !done });

        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        lines.forEach(handleEvent);

        if (done) break;
      }

      handleEvent(buffer);

      if (!receivedToc) {
        throw new Error("The server did not return a table of contents");
      }
    } catch (err) {
      setError(err.message || "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl, youtubeUrl]);

  useEffect(() => {
    const initialUrl = getInitialYouTubeUrl();
    if (!initialUrl || hasAutoStartedRef.current) return;

    hasAutoStartedRef.current = true;
    handleSkimble(initialUrl);
  }, [handleSkimble]);

  const handleShare = async () => {
    if (!videoId) return;

    const shareUrl = getShareUrl(videoId);

    try {
      if (navigator.share) {
        await navigator.share({ url: shareUrl });
        setShareStatus("Shared");
      } else {
        await navigator.clipboard.writeText(shareUrl);
        setShareStatus("Link copied");
      }
      window.setTimeout(() => setShareStatus(""), 1800);
    } catch (err) {
      if (err?.name !== "AbortError") setError("Could not share this link");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-20 text-slate-900 transition-colors dark:bg-slate-950 dark:text-slate-100">
      <div className="mx-auto max-w-6xl px-3 py-6 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-start justify-between gap-4">
              <div>
              {/* <p className="text-sm font-medium uppercase tracking-wide text-slate-500">
                Video → skimmable document
              </p> */}
              <h1 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
                Skimble
              </h1>
              <p className="mt-3 text-base leading-7 text-slate-600 dark:text-slate-400">
                Turn YouTube into skimmable chapter summaries.
              </p>
              </div>
              <button
                type="button"
                onClick={() => setDarkMode((enabled) => !enabled)}
                aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                aria-pressed={darkMode}
                title={darkMode ? "Switch to light mode" : "Switch to dark mode"}
                className="mt-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-slate-200 text-slate-600 transition hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              >
                {darkMode ? (
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <circle cx="12" cy="12" r="4" />
                    <path d="M12 2v2m0 16v2M4.93 4.93l1.42 1.42m11.3 11.3 1.42 1.42M2 12h2m16 0h2M4.93 19.07l1.42-1.42m11.3-11.3 1.42-1.42" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M20.5 15.5A8.5 8.5 0 0 1 8.5 3.5 8.5 8.5 0 1 0 20.5 15.5Z" />
                  </svg>
                )}
              </button>
            </div>

            <div className="mt-5">
              {/* <label className="mb-2 block text-sm font-medium text-slate-700">
                YouTube URL
              </label> */}

              <form
                className="flex flex-col gap-3 sm:flex-row"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleSkimble();
                }}
              >
                <input
                  type="text"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  placeholder="Paste YouTube URL..."
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500"
                />

                <button
                  type="submit"
                  disabled={isLoading || !youtubeUrl.trim()}
                  className="inline-flex items-center justify-center rounded-2xl bg-slate-900 px-5 py-3 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
                >
                  {isLoading ? "Skimbling..." : "Skimble"}
                </button>
              </form>

              {videoId && (
                <div className="mt-3 flex flex-wrap justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => handleSkimble(`https://www.youtube.com/watch?v=${videoId}`, { forceRefresh: true })}
                    disabled={isLoading}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <path d="M20 7v5h-5M4.9 9A7.5 7.5 0 0 1 18 6l2 2M4 17v-5h5m10.1 3A7.5 7.5 0 0 1 6 18l-2-2" />
                    </svg>
                    Re-skimble
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:border-slate-600 dark:hover:bg-slate-800"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      className="h-4 w-4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      aria-hidden="true"
                    >
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <path d="m8.6 10.5 6.8-4M8.6 13.5l6.8 4" />
                    </svg>
                    {shareStatus || "Share"}
                  </button>
                </div>
              )}
            </div>

            {isLoading && (
              <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
                <div className="flex items-center gap-3">
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
                  <div>
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      Building your table of contents
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Segmenting chapters and subtopics...
                    </p>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
                {error}
              </div>
            )}

          </div>

          {videoId && (
            <div
              ref={playerRef}
              className="mx-auto mt-6 aspect-video w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-black shadow-sm"
            >
              <iframe
                ref={playerIframeRef}
                className="h-full w-full"
                src={`https://www.youtube-nocookie.com/embed/${videoId}?enablejsapi=1&playsinline=1&rel=0`}
                title="YouTube video player"
                loading="eager"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                allowFullScreen
              />
            </div>
          )}

          {tocData && (
            <div id="skimble-toc" className="mt-8">
              <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:p-6">
                <div>
                  <div className="relative">
                    <h2 className="mt-1 text-center font-book text-2xl font-bold tracking-tight sm:text-3xl">
                      {tocData.title}
                    </h2>
                    <CopyButton
                      text={tocData.title}
                      label="Copy video title"
                      copyKey="video-title"
                      copiedKey={copiedKey}
                      onCopy={handleCopy}
                      className="absolute -bottom-1 right-0"
                    />
                  </div>
                  <div className="mt-2 flex items-center justify-center gap-3 font-book">
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                      {chapters.length} chapters
                    </span>
                  </div>
                  <h3 className="mt-4 text-center font-book text-xl font-bold text-slate-950 dark:text-slate-100 sm:text-2xl">
                    Summary by Chapters
                  </h3>
                  <div className="mt-3 space-y-7">
                    {chapters.map((chapter, chapterIndex) => (
                      <ChapterCard
                        key={`chapter-${chapterIndex}`}
                        chapter={chapter}
                        chapterIndex={chapterIndex}
                        onJump={handleJumpToTime}
                        onCopy={handleCopy}
                        copiedKey={copiedKey}
                        chapterRef={(element) => { chapterRefs.current[chapterIndex] = element; }}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          <footer className="mt-12 pb-6 text-center text-xs text-slate-400">
            Made by{" "}
            <a
              href="https://x.com/bayramgnb"
              className="transition hover:text-slate-700 dark:hover:text-slate-200"
              target="_blank"
              rel="noreferrer"
            >
              @bayramgnb
            </a>
          </footer>
        </div>
      </div>
      {tocData && chapters.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 shadow-[0_-4px_16px_rgba(15,23,42,0.06)] backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="mx-auto max-w-6xl px-4 py-2.5 sm:px-6 lg:px-8">
            <div className="mb-2 flex items-center justify-between gap-3 text-xs">
              <select
                aria-label="Jump to chapter"
                value={Math.min(readingProgress.chapterIndex, chapters.length - 1)}
                onChange={(event) => {
                  const chapterIndex = Number(event.target.value);
                  chapterRefs.current[chapterIndex]?.scrollIntoView({
                    behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
                    block: "start",
                  });
                }}
                className="min-w-0 flex-1 cursor-pointer rounded-md border border-slate-200 bg-white py-1 pl-2 pr-6 text-xs text-slate-700 outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              >
                {chapters.map((chapter, chapterIndex) => (
                  <option key={chapterIndex} value={chapterIndex}>
                    Chapter {chapterIndex + 1} of {chapters.length}: {chapter.title}
                  </option>
                ))}
              </select>
              <span className="shrink-0 font-medium tabular-nums text-slate-500 dark:text-slate-400">
                {readingProgress.percent}% read
              </span>
            </div>
            <div
              role="progressbar"
              aria-label="Reading progress"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={readingProgress.percent}
              className="h-1.5 overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700"
            >
              <div
                className="h-full rounded-full bg-blue-600 transition-[width] duration-150 dark:bg-blue-400"
                style={{ width: `${readingProgress.percent}%` }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
